import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TryIt } from "@/components/TryIt";
import { ArrowRight, Check, X, Wrench, Repeat, Lightbulb, Zap, Repeat2, TrendingUp, Crosshair, Quote } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const SECTIONS = [
  { label: "Learned", color: "#A855F7", icon: Lightbulb, items: ["Pin the far hip before you cut the knee through"] },
  { label: "Worked", color: "#10B981", icon: Check, items: ["You hit two knee cuts from half guard"] },
  { label: "Failed", color: "#EF4444", icon: X, items: ["Got swept from mount by an upa"] },
  { label: "Fix this", color: "#F59E0B", icon: Wrench, items: ["Base too high. Sink your hips and widen your knees"] },
  { label: "Drill next", color: "#3B82F6", icon: Repeat, items: ["Mount retention vs bridge, 3 × 2 min"] },
];

function CTA({ testid, className = "" }) {
  return (
    <Link to="/register" data-testid={testid} className={`inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#E50914] hover:bg-[#B20710] text-white font-display text-lg uppercase tracking-wide transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${className}`}>
      Create free account <ArrowRight size={18} />
    </Link>
  );
}

function Phone() {
  return (
    <div className="relative mx-auto w-[290px] shrink-0" data-testid="landing-phone">
      <div className="absolute -inset-10 bg-[#E50914]/20 blur-[80px] rounded-full pointer-events-none" />
      <div className="relative rounded-[2.6rem] border-[7px] border-[#222225] bg-[#0A0A0A] p-3.5 pt-8 shadow-2xl shadow-black">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-[#222225]" />
        <div className="px-1.5 mb-3">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717A]">Tue, 14 Oct</div>
          <div className="font-display text-[17px] font-semibold uppercase tracking-tight leading-tight mt-0.5">Half Guard Passing & Mount</div>
        </div>
        <div className="space-y-1.5 pr-1">
          {SECTIONS.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.12 }} className="px-3 py-2 rounded-r-lg border-l-2" style={{ borderColor: s.color, background: `${s.color}12` }}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <s.icon size={10} style={{ color: s.color }} strokeWidth={3} />
                <span className="text-[8.5px] font-bold uppercase tracking-[0.16em]" style={{ color: s.color }}>{s.label}</span>
              </div>
              <div className="text-[11.5px] leading-snug text-[#E4E4E7]">{s.items[0]}</div>
            </motion.div>
          ))}
        </div>
        <div className="flex gap-1.5 mt-3 px-1.5 pb-1">
          {["knee cut", "half guard", "mount"].map((t) => <span key={t} className="text-[10px] px-2 py-1 rounded-md bg-[#161618] border border-[#27272A] text-[#A1A1AA]">{t}</span>)}
        </div>
      </div>
    </div>
  );
}

function NotesPreview() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-[#111113] border border-[#27272A] p-3.5 text-[13px] text-[#A1A1AA] leading-relaxed italic">"hit 2 knee cuts. got swept from mount again base too high i think, couldnt finish armbar kept losing angle"</div>
      <div className="flex justify-center"></div>
      <div className="grid grid-cols-3 gap-2">
        {SECTIONS.slice(1, 4).map((s) => (
          <div key={s.label} className="rounded-lg px-2.5 py-2 border-l-2 text-[10px] font-bold uppercase tracking-wider" style={{ borderColor: s.color, background: `${s.color}14`, color: s.color }}>{s.label}</div>
        ))}
      </div>
    </div>
  );
}

function PatternPreview() {
  const rows = [
    { icon: Repeat2, color: "#EF4444", label: "Keeps happening", title: "Getting upa'd from mount", detail: "4 of your last 6 sessions." },
    { icon: TrendingUp, color: "#10B981", label: "Improving", title: "Knee cut is landing", detail: "Worked in 3 sessions in a row." },
    { icon: Crosshair, color: "#3B82F6", label: "Focus next", title: "Triangle angle", detail: "Cut 90° before locking the shin." },
  ];
  return (
    <div className="space-y-3.5">
      {rows.map((r) => (
        <div key={r.title} className="pl-3.5 border-l-2" style={{ borderColor: r.color }}>
          <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: r.color }}><r.icon size={11} /> {r.label}</div>
          <div className="text-sm font-semibold mt-0.5">{r.title}</div>
          <div className="text-xs text-[#A1A1AA]">{r.detail}</div>
        </div>
      ))}
    </div>
  );
}

function QuizPreview() {
  return (
    <div className="space-y-3">
      <div className="text-[13px] font-semibold leading-snug">Opponent frames on your hips mid-stack pass. What finishes the pass?</div>
      <div className="rounded-xl border border-[#10B981] bg-[#10B981]/10 px-3 py-2.5 text-xs flex items-center justify-between">
        Redirect pressure to their shoulders
        <span className="flex items-center gap-1 font-bold text-[#E50914]"><Zap size={11} className="fill-[#E50914]" />+30 XP</span>
      </div>
      <div className="rounded-xl border border-[#27272A] px-3 py-2.5 flex items-center gap-3 text-xs">
        <span className="font-display text-base text-[#E50914]">1</span>
        <span className="w-6 h-6 rounded-full bg-[#E50914] flex items-center justify-center font-bold text-[11px]">K</span>
        <span className="flex-1 font-semibold">Kai <span className="text-[#71717A] font-normal">· Level 7</span></span>
        <span className="font-display text-base">2,140</span>
      </div>
    </div>
  );
}

const BENEFITS = [
  { n: "01", title: "Your notes, organised", body: "Type how training went, messy is fine. AI sorts it into what you learned, what worked, what failed and what to drill next, so every session counts.", preview: NotesPreview, id: "notes" },
  { n: "02", title: "See what keeps going wrong", body: "Every few sessions, AI reads your whole journal and points out the patterns you'd miss: recurring mistakes, what's improving, and the one thing to focus on.", preview: PatternPreview, id: "patterns" },
  { n: "03", title: "Get sharper, climb the ranks", body: "540 BJJ questions across 12 topics plus quizzes built from your own notes. Earn XP for correct answers and level up against everyone else.", preview: QuizPreview, id: "quizzes" },
];

const REVIEWS = [
  { text: "The output from my first session was really good and captured what I'd written in my messy notes surprisingly well.", featured: true },
  { text: "This is actually really good. I just put my info in from last night's class and I'm going through the results now. Highly recommend." },
  { text: "I used this yesterday. I liked the output and I'm looking forward to seeing what it can do long term." },
];

function Reviews() {
  const [main, ...rest] = REVIEWS;
  return (
    <section className="border-t border-[#1F1F23]" data-testid="landing-reviews">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <motion.div {...reveal} className="text-[11px] uppercase tracking-[0.22em] text-[#71717A] font-semibold mb-8">From people who've tried it</motion.div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <motion.figure {...reveal} className="md:col-span-3 rounded-2xl bg-[#161618] border border-[#27272A] p-7 md:p-9 flex flex-col justify-between" data-testid="landing-review-0">
            <Quote size={26} className="text-[#E50914] mb-5" />
            <blockquote className="font-display text-2xl md:text-3xl font-medium leading-tight tracking-tight">"{main.text}"</blockquote>
            <figcaption className="text-xs text-[#71717A] mt-6">Roll SmartR user · first session</figcaption>
          </motion.figure>
          <div className="md:col-span-2 grid gap-4">
            {rest.map((r, i) => (
              <motion.figure key={i} {...reveal} transition={{ ...reveal.transition, delay: 0.1 * (i + 1) }} className="rounded-2xl bg-[#111113] border border-[#27272A] p-6" data-testid={`landing-review-${i + 1}`}>
                <blockquote className="text-[15px] text-[#D4D4D8] leading-relaxed">"{r.text}"</blockquote>
                <figcaption className="text-xs text-[#71717A] mt-4">Roll SmartR user</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { title: "Train", body: "Roll, drill, get tapped. Same as always." },
  { title: "Type 30 seconds", body: "A quick note in your own words, right after class." },
  { title: "Get better", body: "Organised notes, patterns and quizzes show you what to work on." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="landing-page">
      <header className="sticky top-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1F1F23]" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="font-display text-xl font-bold tracking-tight" data-testid="brand-logo">ROLL <span className="text-[#E50914]">SMARTR</span></div>
          <Link to="/login" data-testid="landing-signin" className="h-10 px-4 flex items-center rounded-full text-sm font-semibold text-[#D4D4D8] hover:text-white border border-[#27272A] hover:border-[#3F3F46] transition-colors">Sign in</Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-20 md:pb-28 flex flex-col md:flex-row md:items-center gap-14 md:gap-16">
        <motion.div {...reveal} className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E50914] font-semibold mb-5">BJJ training journal</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.92]" data-testid="landing-headline">
            Stop forgetting<br />what you learned<br /><span className="text-[#E50914]">on the mats.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-lg mt-6 max-w-md leading-relaxed">Type a quick note after training. Roll SmartR turns it into what worked, what failed and exactly what to drill next, then spots the patterns you'd miss.</p>
          <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
            <CTA testid="landing-cta-hero" />
            <a href="#try" data-testid="landing-try-link" className="text-sm font-semibold text-white underline decoration-[#E50914] underline-offset-4">Try it first, no account</a>
          </div>
          <div className="mt-8 pl-4 border-l-2 border-[#E50914] max-w-md" data-testid="landing-hero-quote">
            <p className="text-sm text-[#D4D4D8] italic leading-relaxed">"This is actually really good… Highly recommend."</p>
            <p className="text-[11px] text-[#71717A] mt-1">Roll SmartR user</p>
          </div>
        </motion.div>
        <Phone />
      </section>

      <TryIt />

      <section className="border-t border-[#1F1F23]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28 space-y-20 md:space-y-28">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.id} {...reveal} className={`flex flex-col md:items-center gap-8 md:gap-16 ${i % 2 ? "md:flex-row-reverse" : "md:flex-row"}`} data-testid={`landing-benefit-${b.id}`}>
              <div className="flex-1">
                <div className="font-display text-sm text-[#E50914] tracking-widest mb-3">{b.n}</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight leading-none">{b.title}</h2>
                <p className="text-[#A1A1AA] mt-4 leading-relaxed max-w-md">{b.body}</p>
              </div>
              <div className="flex-1 w-full max-w-md rounded-2xl bg-[#161618] border border-[#27272A] p-5 hover:border-[#E50914]/40 transition-colors duration-300">
                <b.preview />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Reviews />

      <section className="border-t border-[#1F1F23] bg-[#0D0D0E]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <motion.h2 {...reveal} className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight mb-12">How it works</motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" data-testid="landing-steps">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }} className="flex md:block gap-5">
                <div className="w-11 h-11 shrink-0 rounded-full border border-[#E50914] text-[#E50914] font-display text-lg flex items-center justify-center md:mb-5">{i + 1}</div>
                <div>
                  <div className="font-display text-xl uppercase tracking-tight">{s.title}</div>
                  <p className="text-sm text-[#A1A1AA] mt-1.5 leading-relaxed">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1F1F23]">
        <motion.div {...reveal} className="max-w-5xl mx-auto px-5 md:px-8 py-24 md:py-32 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight leading-[0.95]">Train smarter<br /><span className="text-[#E50914]">from your next roll.</span></h2>
          <p className="text-[#A1A1AA] mt-5">Free to start. Your notes stay private.</p>
          <CTA testid="landing-cta-final" className="mt-9" />
          <div className="mt-5 text-sm text-[#71717A]">Already have an account? <Link to="/login" data-testid="landing-signin-bottom" className="text-white underline decoration-[#E50914] underline-offset-4">Sign in</Link></div>
        </motion.div>
      </section>

      <footer className="border-t border-[#1F1F23]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-8 flex items-center justify-between text-xs text-[#71717A]">
          <span>© Roll SmartR</span>
          <div className="flex gap-5">
            <Link to="/terms" data-testid="landing-terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" data-testid="landing-privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
